import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchTeamComments } from "./services/fetchTeamComments.service";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { createTeamComments } from "./services/createTeamComments.service";
import { IComment } from "./interfaces/IComment";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import styles from "./Comment.module.css";
import {
  BoldItalicUnderlineToggles,
  headingsPlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  MDXEditor,
  MDXEditorMethods,
  Separator,
  thematicBreakPlugin,
  toolbarPlugin,
  UndoRedo,
} from "@mdxeditor/editor";
import { fetchTeamUsers } from "./services/fetchTeamUsers.service";
import { editTeamComments } from "./services/editTeamComments.service";
import { deleteTeamComments } from "./services/deleteTeamComments.service";
import { AiOutlineEdit } from "react-icons/ai";
import { BiSave } from "react-icons/bi";
import { RiDeleteBin7Line } from "react-icons/ri";
import { ModalConfirmWindow } from "../../../../../AdminPanelPage/components/ManageUserTab/components/ModalConfirmWindow/ModalConfirmWindow";

interface ICommentTabProps {
  user: any;
}

const CommentTab: React.FC<ICommentTabProps> = ({ user }) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { regulator } = useParams<{ regulator: string }>();
  const [commentValue, setCommentValue] = useState("");
  const [comments, setComments] = useState<IComment[] | []>([]);
  const [mentionListVisible, setMentionListVisible] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [editorKey, setEditorKey] = useState(0);
  const editorRef = useRef<MDXEditorMethods>(null);
  const [editCommentId, setEditCommentId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<string>("");
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const [editorHeight, setEditorHeight] = useState(155);
  const commentsContainerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const [showRemoveCommentModal, setShowRemoveCommentModal] = useState<{
    visible: boolean;
    commentId: number | null;
  }>({ visible: false, commentId: null });

  const { data: commentList, isLoading } = useQuery({
    queryKey: ["commentList", id],
    queryFn: () => fetchTeamComments(regulator, id),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { mutate: saveComment, isPending } = useMutation({
    mutationFn: (text: string) => createTeamComments(regulator, id, text),
    onSuccess: (newComment) => {
      setComments((prev) => [...prev, newComment]);
      queryClient.setQueryData(["commentList", id], (old: IComment[] = []) => [
        ...old,
        newComment,
      ]);
      clearEditor();
      toast.success(t("docDetails.addCommentToaster"));
    },
    onError: (error: string) => {
      clearEditor();
      toast.error(error);
    },
  });

  const { mutate: editComment } = useMutation({
    mutationFn: ({ text, commentId }: { text: string; commentId: number }) =>
      editTeamComments(regulator, id, text, commentId),
    onSuccess: (updated, vars) => {
      setComments((prev) =>
        prev.map((c) =>
          c.id === (updated?.id ?? vars.commentId)
            ? (updated ?? { ...c, text: vars.text })
            : c,
        ),
      );
      setEditCommentId(null);
      setEditDraft("");
      toast.success(t("docDetails.editCommentToaster") || "Updated.");
    },
    onError: (error: string) => {
      toast.error(error);
    },
  });

  const { mutate: deleteComment } = useMutation({
    mutationFn: (commentId: number) =>
      deleteTeamComments(regulator, id, "", commentId),
    onSuccess: (_res, commentId) => {
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      toast.success(t("docDetails.deleteCommentToaster") || "Deleted.");
    },
    onError: (error: string) => {
      clearEditor();
      toast.error(error);
    },
  });

  const { data: allTeamUserList = [] } = useQuery({
    queryKey: ["allTeamUserList"],
    queryFn: () => fetchTeamUsers(regulator),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (commentsContainerRef.current) {
      commentsContainerRef.current.scrollTop =
        commentsContainerRef.current.scrollHeight;
    }
  }, [comments]);

  useEffect(() => {
    if (commentList) {
      setComments(commentList);
    }
  }, [commentList]);

  useEffect(() => {
    if (!editorContainerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect) {
          setEditorHeight(entry.contentRect.height);
        }
      }
    });

    resizeObserver.observe(editorContainerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  const handleSave = () => {
    const text = commentValue.trim();
    if (!text) return;
    saveComment(text);
  };

  const formatDate = (isoDate: string): string => {
    const date = new Date(isoDate);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const filteredUsers = allTeamUserList
    ?.filter((user: any) => user.name || user.email)
    .filter((user: any) => {
      if (!mentionQuery) return true;
      const q = mentionQuery.toLowerCase();
      return (
        (user.name && user.name.toLowerCase().startsWith(q)) ||
        (user.email && user.email.toLowerCase().startsWith(q))
      );
    });

  const handleMentionSelect = (user: any) => {
    const newText = commentValue.replace(
      /(@\w*)$/,
      `[${user.name !== "" ? user.name : user.email}](mailto:${user.email}) `,
    );
    setCommentValue(newText);
    setEditorKey((prev) => prev + 1);
    setMentionListVisible(false);
    setMentionQuery("");
  };

  useEffect(() => {
    if (!mentionListVisible) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" && filteredUsers.length > 0) {
        event.preventDefault();
        handleMentionSelect(filteredUsers[0]);
      }
      if (event.key === "Escape") {
        setMentionListVisible(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [mentionListVisible, filteredUsers]);

  const clearEditor = () => {
    setCommentValue("");
    setMentionListVisible(false);
    setMentionQuery("");
    editorRef.current?.setMarkdown("");
  };

  const handleDeleteComment = () => {
    if (!showRemoveCommentModal.commentId) return;
    const idToDelete = showRemoveCommentModal.commentId;
    setShowRemoveCommentModal({ visible: false, commentId: null });
    deleteComment(idToDelete);
  };

  return (
    <div className="text-secondary-color h-full min-h-0 flex relative overflow-hidden">
      <div className="w-full relative flex flex-col h-full min-h-0 p-1 pb-2 transition-all duration-300">
        <div
          className={`flex-1 min-h-0 space-y-2 pt-2 px-2 pb-4 ${
            comments?.length ? "overflow-y-auto" : "overflow-y-hidden"
          }`}
          ref={commentsContainerRef}
        >
          {comments?.length === 0 ? (
            <div className="text-gray-400 dark:text-gray-500 italic text-center py-6 text-sm">
              {t("docDetails.noComment")}
            </div>
          ) : (
            comments?.map((comment) => {
              const isOwn = (() => {
                const meId = user?.id ?? null;
                const cId = comment?.user?.id ?? null;
                if (meId != null && cId != null) return meId === cId;

                const meEmail = (user?.email || "").toLowerCase();
                const cEmail = (comment?.user?.email || "").toLowerCase();
                if (meEmail && cEmail) return meEmail === cEmail;

                return false;
              })();
              const isEditing = editCommentId === comment.id;
              return (
                <div
                  key={comment.id ?? `comment-${comment.created_at}`}
                  className="w-full p-3 rounded-lg bg-slate-100 dark:bg-slate-600 text-gray-800 dark:text-white"
                >
                  <div className="w-full">
                    <div className="flex items-center justify-between w-full text-sm font-semibold text-gray-600 dark:text-gray-200">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="truncate">{comment.user.name}</span>
                        <div className="h-5 w-px bg-gray-700" />
                        <span className="text-xs text-gray-600 dark:text-gray-300">
                          {formatDate(comment.created_at)}
                        </span>
                      </div>

                      {isOwn && (
                        <div className="flex items-center shrink-0">
                          {!isEditing ? (
                            <>
                              <button
                                onClick={() => {
                                  setEditCommentId(comment.id);
                                  setEditDraft(comment.text || "");
                                }}
                                className="p-1.5 hover:opacity-80 transition"
                                aria-label={t("docDetails.editComment")}
                              >
                                <AiOutlineEdit size={16} className="icon-gray" />
                              </button>

                              <button
                                onClick={() =>
                                  setShowRemoveCommentModal({
                                    visible: true,
                                    commentId: comment.id,
                                  })
                                }
                                aria-label={t("docDetails.deleteComment")}
                                className={`p-1.5 transition text-del-red hover:opacity-80 hover:text-red-700`}
                              >
                                <RiDeleteBin7Line size={16} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  const text = editDraft.trim();
                                  if (!text) return;
                                  editComment({ text, commentId: comment.id });
                                }}
                                className="p-1.5 hover:opacity-80 transition"
                                aria-label={t("saveTooltip")}
                              >
                                <BiSave size={16} className="icon-gray" />
                              </button>

                              <button
                                onClick={() => {
                                  setEditCommentId(null);
                                  setEditDraft("");
                                }}
                                className="p-1.5 hover:opacity-80 transition text-gray-700 dark:text-white icon-gray"
                                aria-label={t("feedback.cancel")}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.89 4.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.89a1 1 0 0 0 1.41-1.41L13.41 12l4.89-4.89a1 1 0 0 0 0-1.4z"/></svg>
                              </button>
                            </>
                          )}
                        </div>
                      )}
                      
                    </div>

                    <div className="text-base mt-1">
                      {isEditing ? (
                        <MDXEditor
                          markdown={editDraft}
                          onChange={(md: string) => setEditDraft(md)}
                          contentEditableClassName={`${styles.editor} ${styles.markdownBlock} w-full text-secondary-color border border-b border-l border-r border-gray-300 dark:border-gray-300 bg-white dark:bg-gray-800 mb-2 min-h-[56px] max-h-[240px] overflow-y-auto leading-5 py-2 px-3 box-border`}
                          plugins={[
                            headingsPlugin(),
                            listsPlugin(),
                            thematicBreakPlugin(),
                            linkPlugin(),
                            linkDialogPlugin(),
                            toolbarPlugin({
                              toolbarContents: () => (
                                <div className="flex flex-wrap w-full text-xs">
                                  <UndoRedo /> <Separator />
                                  <BoldItalicUnderlineToggles /> <Separator />{" "}
                                  <ListsToggle />
                                </div>
                              ),
                            }),
                          ]}
                        />
                      ) : (
                        <Markdown
                          className={`${styles.markdownCommentBlock} max-w-none text-sm`}
                          rehypePlugins={[rehypeRaw]}
                          remarkPlugins={[remarkGfm, remarkBreaks]}
                          components={{
                            text({ children }) {
                              const text =
                                Array.isArray(children) &&
                                typeof children[0] === "string"
                                  ? children[0]
                                  : "";
                              const parts = text.split(
                                /(\[@?[\w\s.]+\]\(mailto:[^)]+\)|@\w+)/g,
                              );
                              return (
                                <>
                                  {parts.map((part, i) =>
                                    /\[@?[\w\s.]+\]\(mailto:[^)]+\)|@\w+/.test(
                                      part,
                                    ) ? (
                                      <span
                                        key={i}
                                        className="bg-green-200 text-green-900 rounded-md pr-1 pl-0 font-semibold"
                                      >
                                        {part}
                                      </span>
                                    ) : (
                                      <span key={i}>{part}</span>
                                    ),
                                  )}
                                </>
                              );
                            },
                          }}
                        >
                          {comment.text}
                        </Markdown>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {mentionListVisible && (
          <div
            className="absolute left-1/2
      bottom-24
      -translate-x-1/2 z-50 bg-white dark:bg-gray-800 border rounded w-64 max-h-[400px] overflow-y-auto"
          >
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user: any) => (
                <div
                  key={user.email || user.name}
                  className="px-4 py-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-gray-700 border-b"
                  onClick={() => handleMentionSelect(user)}
                >
                  <p className="font-semibold text-sm">{user.name}</p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                </div>
              ))
            ) : (
              <div className="px-4 py-2 text-gray-500 dark:text-gray-400 text-sm">
                {t("docDetails.noMatches")}
              </div>
            )}
          </div>
        )}

        <div
          ref={editorContainerRef}
          className="bg-white dark:bg-gray-800 w-full px-4 py-2 border-t border-gray-400 dark:border-gray-300 text-sm flex-shrink-0"
        >
          <MDXEditor
            key={editorKey}
            ref={editorRef}
            markdown={commentValue}
            placeholder={t("docDetails.commentsPlaceholder")}
            onChange={(data: string) => {
              setCommentValue(data);

              const lastLine = data.split(/\n/).pop() ?? "";
              const atMatch = lastLine.match(/(?:^|\s)@(\w*)$/);

              if (atMatch) {
                const query = atMatch[1];
                setMentionQuery(query);
                setMentionListVisible(true);
              } else {
                setMentionListVisible(false);
                setMentionQuery("");
              }
            }}
            contentEditableClassName={`${styles.editor} ${styles.markdownBlock} text-secondary-color border-b border-l border-r mb-1
   min-h-[56px] max-h-[200px] overflow-y-auto leading-5 py-2 px-3 box-border`}
            plugins={[
              headingsPlugin(),
              listsPlugin(),
              thematicBreakPlugin(),
              linkPlugin(),
              linkDialogPlugin(),
              toolbarPlugin({
                toolbarContents: () => (
                  <div className="flex flex-wrap w-full text-xs">
                    <UndoRedo /> <Separator />
                    <BoldItalicUnderlineToggles /> <Separator /> <ListsToggle />
                  </div>
                ),
              }),
            ]}
          />
          <div className="flex justify-end gap-2">
            <button
              className="px-5 py-1.5 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500 transition"
              type="button"
              onClick={clearEditor}
            >
              {t("adminPanel.cancel")}
            </button>
            <button
              className={`px-5 py-1.5 text-white rounded-md transition ${
                !commentValue.trim()
                  ? "bg-gray-400 opacity-50 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
              }`}
              type="button"
              disabled={!commentValue.trim() || isPending}
              onClick={handleSave}
            >
              {t("adminPanel.save")}
            </button>
          </div>
        </div>
      </div>
      {showRemoveCommentModal.visible && (
        <ModalConfirmWindow
          setShowModal={setShowRemoveCommentModal}
          handleDeleteUser={handleDeleteComment}
          message={t("docDetails.deleteCommentMsg")}
        />
      )}
    </div>
  );
};

export { CommentTab };
